// Aggregated credit usage stats: balance, lifetime spent, lifetime purchased,
// and rolling 30-day spend. Backed by the existing `credit_transactions` and
// `profiles` tables (RLS scopes everything to the signed-in user).
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/hooks/useCredits";

export interface CreditUsageStats {
  /** Current balance from `profiles.credits`. */
  balance: number | null;
  /** Lifetime credits spent (sum of negative deltas, as a positive number). */
  lifetimeSpent: number;
  /** Lifetime credits purchased (sum of `purchase` deltas). */
  lifetimePurchased: number;
  /** Lifetime free credits earned (signup bonus, rewarded ads, refunds, etc.). */
  lifetimeGranted: number;
  /** Credits spent in the last 30 days. */
  spent30d: number;
  /** Credits earned (any positive delta) in the last 30 days. */
  earned30d: number;
  loading: boolean;
}

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export const useCreditUsage = (): CreditUsageStats & { refresh: () => void } => {
  const { user } = useAuth();
  const { credits } = useCredits();
  const [lifetimeSpent, setLifetimeSpent] = useState(0);
  const [lifetimePurchased, setLifetimePurchased] = useState(0);
  const [lifetimeGranted, setLifetimeGranted] = useState(0);
  const [spent30d, setSpent30d] = useState(0);
  const [earned30d, setEarned30d] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setLifetimeSpent(0);
      setLifetimePurchased(0);
      setLifetimeGranted(0);
      setSpent30d(0);
      setEarned30d(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    // Pull every transaction (typical user = a few dozen rows; well under
    // the 1000-row Supabase default). RLS keeps this scoped to the user.
    const { data } = await supabase
      .from("credit_transactions")
      .select("delta, reason, created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(1000);

    let spent = 0;
    let purchased = 0;
    let granted = 0;
    let recentSpent = 0;
    let recentEarned = 0;
    const cutoff = Date.now() - THIRTY_DAYS_MS;

    (data ?? []).forEach((row) => {
      const delta = Number(row.delta) || 0;
      const ts = new Date(row.created_at).getTime();
      const recent = ts >= cutoff;
      if (delta < 0) {
        spent += -delta;
        if (recent) recentSpent += -delta;
      } else if (delta > 0) {
        if (row.reason === "purchase") purchased += delta;
        else granted += delta;
        if (recent) recentEarned += delta;
      }
    });

    setLifetimeSpent(spent);
    setLifetimePurchased(purchased);
    setLifetimeGranted(granted);
    setSpent30d(recentSpent);
    setEarned30d(recentEarned);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Realtime: any new credit_transaction for this user re-aggregates.
  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel(`credit-usage-${user.id}-${crypto.randomUUID()}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "credit_transactions",
          filter: `user_id=eq.${user.id}`,
        },
        () => refresh(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [user, refresh]);

  return {
    balance: credits,
    lifetimeSpent,
    lifetimePurchased,
    lifetimeGranted,
    spent30d,
    earned30d,
    loading,
    refresh,
  };
};
